import React from 'react'
import { css } from '@emotion/core'

import { useMakeTestId } from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import { curry } from '@divvy-web/utils'

import { useAuth } from '@sputnik/auth'
import InviteCollaboratorsLink from '@sputnik/components/InviteCollaboratorsLink'
import BillSideBarIcon from '@sputnik/components/legacy/BillSideBarIcon/BillSideBarIcon'
import { getIsDSABusinessOnly, getIsDSAWebBank } from '@sputnik/components/utils'
import { PAGE_NAME_STRINGS } from '@sputnik/resources/constants'

import SideSectionContent from './SideSectionContent/SideSectionContent'

interface ReviewsSideSectionProps {
  dsaVersion: string
  inviteCollaborators?: boolean
  pageName: string
}

const ReviewsSideSection = ({ dsaVersion, inviteCollaborators, pageName }: ReviewsSideSectionProps) => {
  const makeTestId = useMakeTestId('ReviewsSideSection')
  const { PAGE_APPLICATION_SUBMITTED } = PAGE_NAME_STRINGS
  const { email } = useAuth()
  const isApplicationSubmitted = pageName === PAGE_APPLICATION_SUBMITTED
  const shouldShowAuthorizedSignerProTip = !getIsDSABusinessOnly(dsaVersion)
  const shouldShowWebBankTip = getIsDSAWebBank(dsaVersion)

  return (
    <div
      className='fs-unmask'
      css={sectionCss({
        isApplicationSubmitted,
      })}
    >
      <div
        className='reviews-section-wrapper'
        data-testid={makeTestId('')}
      >
        <BillSideBarIcon />
        <div className='reviews-and-tips'>
          <SideSectionContent
            pageName={pageName}
            shouldShowAuthorizedSignerProTip={shouldShowAuthorizedSignerProTip}
            shouldShowWebBankTip={shouldShowWebBankTip}
          />
        </div>
        {inviteCollaborators && (
          <div className='signed-in-info'>
            {isApplicationSubmitted || (
              <div className='invite-collaborators'>
                <InviteCollaboratorsLink />
              </div>
            )}
            <div
              className='signed-as fs-mask'
              data-testid={makeTestId('signed-as')}
            >
              <FormattedMessage
                defaultMessage='You are signed in as {email}'
                id='sputnik.inviteCollaboratorsUtils__sy+pv5'
                values={{ email }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ReviewsSideSection

const sectionCss = curry(
  ({ isApplicationSubmitted }, theme) => css`
    height: 100%;

    .reviews-section-wrapper {
      display: flex;
      height: 100%;
      flex-direction: column;
      align-items: center;
      justify-content: space-between;
      margin: 0 var(--tri-space-500);
    }

    .reviews-and-tips {
      display: flex;
      height: 90%;
      flex-direction: column;
    }

    @media (max-height: 440px) {
      .large-image {
        ${isApplicationSubmitted && 'padding-bottom: 0;'}
      }

      .reviews-and-tips {
        padding-top: var(--tri-space-500);
        padding-bottom: var(--tri-space-500);
      }
    }

    .signed-in-info {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      gap: var(--tri-space-100);
      align-items: center;
      align-self: center;
      padding-bottom: var(--tri-space-400);
    }

    .signed-in-info > .signed-as {
      text-align: center;
      ${theme.type.triFontDesktopBodySmall}
    }

    .invite-collaborators > button > span {
      color: var(--tri-color-interactive-foreground-action-hover);
    }
  `,
)
